import type { MarkType } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";

import type { RichTextCellType } from "../../../../../shared/types/worksheet";

import type { AuthoredTransaction, RemoteSelectionData } from "./types";

export type OpWithIndex = { index: number; op: RichTextCellType["ops"][number] };

/**
 * Strip ops missing "insert" or containing "retain" from stored doc data.
 * Returns valid and invalid ops with index.
 */
export function partitionOps(ops: RichTextCellType["ops"] = []): {
  validOps: OpWithIndex[];
  invalidOps: OpWithIndex[];
} {
  const invalidIndexes = ops.reduce<number[]>((acc, op, index) => {
    if (op.insert == null || op.retain) acc.push(index);
    return acc;
  }, []);

  if (invalidIndexes.length === 0) {
    return {
      validOps: ops.map((op, index) => ({ index, op })),
      invalidOps: [],
    };
  }

  const validOps = ops
    .map((op, index) => ({ index, op }))
    .filter(({ index }) => !invalidIndexes.includes(index));
  const invalidOps = invalidIndexes.map((index) => ({ index, op: ops[index] }));

  return { validOps, invalidOps };
}

/**
 * Check if a mark (formatting like bold, italic, code) is active in the current selection
 *
 * Marks can be applied to:
 * - A text selection (when text is selected)
 * - The cursor position (stored marks, applied to the next character typed)
 *
 * @param state - The current editor state
 * @param type - The mark type to check (e.g., schema.marks.bold for bold)
 * @returns true if the mark is active in the current selection/cursor position
 */
export function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  // If selection is empty (just a cursor), check stored marks or marks at cursor position
  if (empty) return !!type.isInSet(state.storedMarks || $from.marks());
  // If text is selected, check if the mark exists anywhere in the selected range
  return state.doc.rangeHasMark(from, to, type);
}

/**
 * Maps remote cursor positions through transaction mappings to keep them visually stable when
 * the document changes. Called for both local and remote document updates.
 *
 * ShareDB delivers presence (cursor positions) and ops (content changes) separately.
 * Without remapping, a doc change can make stored positions stale, causing remote cursors to jump
 * until the next presence update corrects them. Presence updates are authoritative and always
 * overwrite these mapped positions when they arrive.
 *
 * Returns a new map with updated selections merged in, or null if no positions changed.
 */
export function remapSelections(
  selections: Map<string, RemoteSelectionData>,
  authoredTransactions: AuthoredTransaction[],
): Map<string, RemoteSelectionData> | null {
  if (selections.size === 0 || authoredTransactions.length === 0) return null;

  // Find the earliest position affected by any step across all transactions
  let earliestChangePos = Infinity;
  for (const { transaction } of authoredTransactions) {
    for (const step of transaction.steps) {
      if ("from" in step && typeof step.from === "number") {
        earliestChangePos = Math.min(earliestChangePos, step.from);
      }
    }
  }

  // No positional changes so no need to map selections
  if (earliestChangePos === Infinity) return null;

  const updatedSelections = new Map<string, RemoteSelectionData>();
  for (const [srcPresenceId, selection] of selections) {
    // Remap if selection extends to or past the change position
    // (selections entirely before the change don't need remapping)
    if (selection.to >= earliestChangePos) {
      // Map through each transaction individually so we can apply per-author bias.
      // Bias only matters when a cursor is at the exact insertion point: the op
      // author's cursor should follow their own typing (bias 1), but bystanders'
      // cursors should stay put before the new content (bias -1). For positions
      // before or after the insertion, both biases produce the same result.
      let mappedFrom = selection.from;
      let mappedTo = selection.to;
      for (const { transaction, authorUserId } of authoredTransactions) {
        const bias = authorUserId && selection.userId === authorUserId ? 1 : -1;
        mappedFrom = transaction.mapping.map(mappedFrom, bias);
        mappedTo = transaction.mapping.map(mappedTo, bias);
      }

      // Only update if positions changed (may collapse to a point if content was deleted)
      if (mappedFrom !== selection.from || mappedTo !== selection.to) {
        updatedSelections.set(srcPresenceId, { ...selection, from: mappedFrom, to: mappedTo });
      }
    }
  }

  if (updatedSelections.size === 0) return null;
  return new Map([...selections, ...updatedSelections]);
}
